import React, { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { formatMedida, formatDateTime, formatDate } from "@/lib/format";
import {
  X, Loader2, PackagePlus, ScanLine, Undo2, RotateCcw, Trash2, Pencil, MapPin, FileText, ArrowDownToLine, Truck,
} from "lucide-react";
import { toast } from "sonner";
import { EntradaDetalheModal } from "@/components/EntradaDetalheModal";

const TIPO_META = {
  entrada:     { label: "Entrada",     icon: PackagePlus, color: "#16A34A", bg: "#F0FDF4" },
  saida:       { label: "Saída",       icon: ScanLine,    color: "#2563EB", bg: "#EFF6FF" },
  devolucao:   { label: "Devolução",   icon: Undo2,       color: "#CA8A04", bg: "#FEFCE8" },
  estorno:     { label: "Estorno",     icon: RotateCcw,   color: "#CA8A04", bg: "#FEFCE8" },
  exclusao:    { label: "Exclusão",    icon: Trash2,      color: "#DC2626", bg: "#FEF2F2" },
  localizacao: { label: "Localização", icon: MapPin,      color: "#2563EB", bg: "#EFF6FF" },
  correcao:    { label: "Correção",    icon: Pencil,      color: "#7C3AED", bg: "#F5F3FF" },
  retorno:     { label: "Retorno",     icon: Undo2,       color: "#CA8A04", bg: "#FEFCE8" },
  cancelamento:{ label: "Cancelamento",icon: Trash2,      color: "#DC2626", bg: "#FEF2F2" },
};

export function RoloTimelineModal({ roloId, open, onClose, onOpenEntrada, onOpenRomaneio }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openEntradaId, setOpenEntradaId] = useState(null);

  useEffect(() => {
    if (!open || !roloId) return;
    setLoading(true);
    setData(null);
    api.get(`/rolos/${roloId}/timeline`)
      .then((r) => setData(r.data))
      .catch((e) => {const _m=apiError(e); if(_m) toast.error(_m);})
      .finally(() => setLoading(false));
  }, [open, roloId]);

  if (!open) return null;

  const rolo = data?.rolo;
  const entrada = data?.entrada;
  const romaneio = data?.romaneio;
  const movs = data?.movimentacoes || [];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
      data-testid="rolo-timeline-modal"
    >
      <div
        className="max-h-[88vh] w-full max-w-2xl overflow-y-auto rounded-md bg-white p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText size={22} className="text-slate-700" />
            <h2 className="text-xl font-bold text-slate-900">Histórico do rolo</h2>
          </div>
          <button
            onClick={onClose}
            data-testid="rolo-timeline-close"
            className="rounded-md p-1 text-slate-400 hover:bg-slate-100"
          >
            <X size={20} />
          </button>
        </div>

        {loading && (
          <div className="flex justify-center py-16 text-slate-400">
            <Loader2 className="animate-spin" />
          </div>
        )}

        {!loading && rolo && (
          <>
            {/* Cabeçalho do rolo */}
            <div className="mb-4 rounded-md border-2 border-slate-200 bg-slate-50 p-4">
              <div className="flex flex-wrap items-baseline gap-2">
                <span className="font-mono text-xl font-bold text-slate-900" data-testid="timeline-rolo-codigo">
                  {rolo.codigo}
                </span>
                <span className="text-sm text-slate-600">
                  {rolo.tipo} {rolo.cor}
                </span>
                <span className="text-sm text-slate-500">· {rolo.fornecedor_nome}</span>
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-600">
                <span className="font-mono font-semibold text-slate-800">
                  {formatMedida(rolo.peso_atual_kg, rolo.unidade)}
                </span>
                <span>
                  Status: <strong className="text-slate-800">{rolo.status}</strong>
                </span>
                {rolo.localizacao && (
                  <span className="flex items-center gap-1">
                    <MapPin size={14} className="text-slate-400" />
                    {rolo.localizacao}
                  </span>
                )}
              </div>
            </div>

            {/* Documentos vinculados */}
            <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {/* Entrada */}
              <div className="rounded-md border-2 border-green-200 bg-green-50 p-3" data-testid="timeline-doc-entrada">
                <div className="mb-1 flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-green-700">
                  <ArrowDownToLine size={14} /> Entrada
                </div>
                {entrada ? (
                  <button
                    onClick={() => {
                      if (onOpenEntrada) onOpenEntrada(entrada.id);
                      else setOpenEntradaId(entrada.id);
                    }}
                    className="text-left"
                    data-testid="timeline-entrada-link"
                  >
                    <div className="font-mono text-lg font-bold text-green-800 hover:underline">
                      {entrada.codigo}
                    </div>
                    <div className="text-xs text-green-700">Ver documento →</div>
                  </button>
                ) : (
                  <div className="text-sm italic text-slate-500">
                    Sem documento (rolo cadastrado antes do rastreio)
                  </div>
                )}
                <div className="mt-1 text-xs text-slate-500">
                  {formatDate(rolo.data_entrada)}
                </div>
              </div>

              {/* Saída */}
              <div className="rounded-md border-2 border-blue-200 bg-blue-50 p-3" data-testid="timeline-doc-saida">
                <div className="mb-1 flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-blue-700">
                  <Truck size={14} /> Saída
                </div>
                {romaneio ? (
                  <button
                    type="button"
                    onClick={() => {
                      if (onOpenRomaneio) onOpenRomaneio(romaneio);
                      else if (typeof window !== "undefined") {
                        window.open(`/romaneio?codigo=${romaneio.codigo}`, "_self");
                      }
                    }}
                    className="w-full text-left"
                    data-testid="timeline-romaneio-link"
                  >
                    <div className="font-mono text-lg font-bold text-blue-800 hover:underline">
                      REF {romaneio.ref}
                    </div>
                    <div className="text-xs text-blue-700 hover:underline">
                      {romaneio.codigo} · {romaneio.cliente || "—"} · {romaneio.destino}
                    </div>
                    <div className="mt-1 text-xs text-slate-500">
                      {formatDate(romaneio.data)} · <span className="font-semibold">{romaneio.status}</span>
                    </div>
                    <div className="mt-1 text-xs text-blue-700">Ver romaneio →</div>
                  </button>
                ) : (
                  <div className="text-sm italic text-slate-500">
                    Rolo ainda em estoque
                  </div>
                )}
              </div>
            </div>

            {/* Timeline */}
            <div className="mb-2 text-sm font-semibold text-slate-700">
              Linha do tempo ({movs.length} evento{movs.length === 1 ? "" : "s"})
            </div>
            <div className="space-y-2" data-testid="timeline-movs">
              {movs.length === 0 && (
                <div className="rounded-md border-2 border-dashed border-slate-200 p-4 text-center text-sm text-slate-400">
                  Sem eventos registrados.
                </div>
              )}
              {movs.map((m) => {
                const meta = TIPO_META[m.tipo] || TIPO_META.correcao;
                return (
                  <div
                    key={m.id}
                    className="flex items-start gap-3 rounded-md border-2 border-slate-200 bg-white p-3"
                  >
                    <div
                      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md"
                      style={{ background: meta.bg, color: meta.color }}
                    >
                      <meta.icon size={16} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span
                          className="rounded-sm px-2 py-0.5 text-xs font-bold uppercase"
                          style={{ background: meta.bg, color: meta.color }}
                        >
                          {meta.label}
                        </span>
                        {m.ref_vinculada && (
                          <span className="rounded-sm bg-slate-100 px-1.5 py-0.5 text-xs font-medium text-slate-600">
                            {m.ref_vinculada}
                          </span>
                        )}
                        <span className="font-mono text-xs font-bold text-slate-700">
                          {formatMedida(m.peso, m.unidade)}
                        </span>
                      </div>
                      {m.detalhe && (
                        <div className="mt-0.5 text-sm text-slate-600">{m.detalhe}</div>
                      )}
                      <div className="mt-0.5 text-xs text-slate-400">
                        {formatDateTime(m.data_hora)} · por {m.usuario_nome}
                        {m.destino ? ` · destino ${m.destino}` : ""}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      <EntradaDetalheModal
        entradaId={openEntradaId}
        open={!!openEntradaId}
        onClose={() => setOpenEntradaId(null)}
        zClass="z-[60]"
      />
    </div>
  );
}
